import { useCallback, useEffect, useState } from 'react';
import Page from '../../../../components/page/Page';
import Header from '../../../../components/administration/locations/Header';
import { IconButton, Paper, Box } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import useThemeMediaQuery from '../../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../../core/router/navigation';
import { useMeCore } from '../../../../../core/modules/me';
import { useGlobalCore } from '../../../../../core/modules/global';
import CreateDialog from '../../../../components/administration/locations/CreateDialog';
import UpdateDialog from '../../../../components/administration/locations/UpdateDialog';
import { useCompanyCore } from '../../../../../core/modules/company';
import EditIcon from '@mui/icons-material/Edit';
import { UPDATE_LOCATION_DIALOG } from '../../../../../core/constants/dialog';
import { useLocationCore } from '../../../../../core/modules/location';
import { Location } from '../../../../../core/interfaces/location.interface';
import { useMasterCore } from '../../../../../core/modules/master';
import { useWarehouseCore } from '../../../../../core/modules/warehouse';

const Locations = () => {
  const { user } = useMeCore();
  const { openDialog } = useGlobalCore();
  const { companyId } = useCompanyCore();
  const { locations, setLocation, getLocations } = useLocationCore();
  const { getTypes, locationTypes } = useMasterCore();
  const { getWarehouses } = useWarehouseCore();
  const { goToWelcome } = useNavigationCore();

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const [gridReady, setGridReady] = useState(false);

  useEffect(() => {
    // Render diferido para evitar ResizeObserver loop al cambiar de vista
    const t = setTimeout(() => setGridReady(true), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (
      !user ||
      (
        !user.privileges.superAdmin &&
        !user.privileges.readLocation &&
        !user.privileges.writeLocation
      )
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  useEffect(() => {
    if (companyId) {
      getLocations(companyId);
      // Cargar tipos de ubicación para mostrar la descripción en la grilla
      getTypes(companyId, 'location');
    }
  }, [companyId, getLocations, getTypes]);

  const selectLocation = useCallback((location: Location) => {
    if (companyId) {
      getWarehouses(companyId);
      getTypes(companyId, 'location');
      getTypes(companyId, 'channel');
    }
    setLocation(location);
    openDialog(UPDATE_LOCATION_DIALOG);
  }, [companyId, getTypes, getWarehouses, openDialog, setLocation]);

  const columns: GridColDef[] = [
    {
      field: 'code',
      headerName: 'Código',
      cellClassName: 'flex items-center p-8',
      flex: 1,
    },
    {
      field: 'warehouse_code',
      headerName: 'Código Bodega',
      cellClassName: 'flex items-center p-8',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (params.row as any).warehouse_code || '-',
    },
    {
      field: 'location_type_id',
      headerName: 'Tipo de ubicación',
      cellClassName: 'flex items-center p-8',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const row: any = params.row;
        const t = (locationTypes || []).find((t) => t.id === row.location_type_id);
        return t ? (t.description || (t as any).name) : row.location_type_id;
      },
    },
    {
      field: 'actions',
      headerName: '',
      cellClassName: 'flex items-center justify-end p-8',
      renderCell: (params: GridRenderCellParams) => (
        <IconButton aria-label="edit" onClick={() => selectLocation(params.row)}>
          <EditIcon />
        </IconButton>
      )
    },
  ];

  return (
    <>
      <Page
        header={<Header />}
        content={
          <div className="w-full my-16 px-24 md:px-32">
            <Paper className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden">
              <div className="w-full p-32">
                {gridReady && (
                  <Box sx={{ height: '70vh', overflow: 'auto' }}>
                    <DataGrid
                      rows={locations}
                      columns={columns}
                      sx={{ border: 0 }}
                      disableColumnSorting
                      disableColumnFilter
                      disableRowSelectionOnClick
                      disableMultipleRowSelection
                      hideFooter
                      getRowHeight={() => 'auto'}
                      pagination={undefined}
                    />
                  </Box>
                )}
              </div>
            </Paper>
          </div>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <CreateDialog />
      <UpdateDialog />
    </>
  );
};

export default Locations;
